import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  AuthStatusResponse,
  companyAuthStatusApiClient,
} from '@src/api/company/auth/companyAuthStatusApiClient';
import { CompanyUser, companyUserApiClient } from '@src/api/company/auth/companyUserApiClient';
import { Pagination } from '@src/api/types/pagination';
import PaginationComponent from '@src/components/Pagination';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button.tsx';
import InputSearch from '@src/ui/elements/form/InputSearch';
import AuthIcon from '@src/ui/elements/table/AuthIcon';
import StatusTip from '@src/ui/elements/table/StatusTip';
import Th from '@src/ui/elements/table/Th.tsx';
import Title from '@src/ui/elements/typography/Title.tsx';
import React, { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

const CompanyUserListForm: React.FC = () => {
  const [loginStatus, setLoginStatus] = useState<AuthStatusResponse>();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [pagination, setPagination] = useState<Pagination>();
  const [searchValue, setSearchValue] = useState<string>('');

  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const sort = searchParams.get('sort') || 'created_at';
  const direction = searchParams.get('direction') || 'desc';
  const page = searchParams.get('page');
  const keyword = searchParams.get('keyword');

  useEffect(() => {
    const fetchLoginStatus = async () => {
      const res = await companyAuthStatusApiClient.status();
      handleApiResponse(res, (data) => {
        setLoginStatus(data);
      });
    };
    fetchLoginStatus();
  }, []);

  const loadCompanyUsers = async () => {
    const params = {
      sort: sort || undefined,
      direction: direction || undefined,
      page: page || undefined,
      keyword: keyword || undefined,
    };

    const res = await companyUserApiClient.index(params);
    handleApiResponse(res, (data) => {
      setUsers(data.data);
      setSearchValue(keyword || '');
      setPagination(data.pagination);
    });
  };

  useEffect(() => {
    loadCompanyUsers();
  }, [sort, direction, page, keyword]);

  const handleSort = (
    key: 'created_at' | 'email' | 'enabled' | 'expires_at' | 'fullname_kana' | 'companyRole.name',
  ) => {
    const newDirection = sort === key && direction === 'asc' ? 'desc' : 'asc';
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('sort', key);
    newSearchParams.set('direction', newDirection);
    newSearchParams.set('page', '1'); // ソート変更時はページを1にリセット
    setSearchParams(newSearchParams);
  };

  const handlePageChange = (page: number) => {
    const query = new URLSearchParams(location.search);
    query.set('page', page.toString());
    navigate({ search: query.toString() });
  };

  const handleSearch = () => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set('keyword', searchValue);
    newSearchParams.set('page', '1');
    setSearchParams(newSearchParams);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };
  return (
    <main className='px-6 pb-12'>
      <div>
        <div className='flex flex-col'>
          <div className='mt-12 flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>{loginStatus?.company?.name}</Title>
            </div>
            <div className='w-[250px]'>
              <InputSearch
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSubmit={handleSearch}
                onKeyDown={handleSearchKeyDown}
                onClear={() => {
                  setSearchValue('');
                  setSearchParams(new URLSearchParams());
                }}
              />
            </div>
          </div>
          <div className='mt-4 flex justify-end'>
            <Button
              size='s'
              onClick={() => navigate('/company-users/create')}
            >
              新規作成
            </Button>
          </div>
        </div>
        {keyword && (
          <p className='overflow-wrap-anywhere mt-8 text-sm'>
            「{keyword}」で絞り込んだ結果：{pagination?.total}件
          </p>
        )}
        {!keyword && pagination?.total === 0 && (
          <p className='mt-4 text-sm'>データがありません。</p>
        )}
        <div className='mt-4 overflow-auto'>
          <table className='size-full min-w-[900px] table-fixed text-sm [&_tbody_tr>*]:p-0 [&_thead_th]:p-0'>
            <colgroup>
              <col className='w-[132px]' />
              <col className='w-[132px]' />
              <col className='w-[132px]' />
              <col />
              <col className='w-[120px]' />
              <col className='w-[80px]' />
              <col className='w-[160px]' />
            </colgroup>
            <thead className='h-full border border-solid border-gray-200 bg-gray-50'>
              <tr>
                <Th
                  sortable
                  sortOrder={sort === 'created_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('created_at')}
                >
                  登録日
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'fullname_kana' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('fullname_kana')}
                >
                  氏名
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'companyRole.name' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('companyRole.name')}
                >
                  権限
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'email' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('email')}
                >
                  メールアドレス
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'enabled' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('enabled')}
                >
                  ステータス
                </Th>
                <Th
                  sortable
                  sortOrder={sort === 'expires_at' ? (direction as 'asc' | 'desc') : null}
                  onClick={() => handleSort('expires_at')}
                >
                  認証
                </Th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0
                ? users.map((user) => (
                    <tr
                      key={user.id}
                      className='[&:nth-child(2n)]:bg-gray-10'
                    >
                      <td>
                        <div className='truncate px-3 py-6'>{user.created_on}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>
                          {user.family_name} {user.first_name}
                        </div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{user.company_role_name}</div>
                      </td>
                      <td>
                        <div className='truncate px-3 py-6'>{user.email}</div>
                      </td>
                      <td>
                        <div className='px-3 py-6'>
                          <StatusTip
                            label={user.status ? '有効' : '無効'}
                            active={user.status}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='px-3 py-6'>
                          <AuthIcon active={user.authenticated} />
                        </div>
                      </td>
                      <td>
                        <div className='flex justify-end px-4'>
                          <Button
                            size='s'
                            variant='quaternary'
                            outline
                            onClick={() =>
                              navigate(
                                generatePath(COMPANY_URL.COMPANY_USERS_EDIT, { id: user.id }),
                              )
                            }
                          >
                            編集する
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
          {!keyword && pagination?.total === 0 ? (
            <p className='mt-4 text-sm'>データがありません。</p>
          ) : null}
        </div>
        {pagination && pagination.total !== 0 && (
          <div className='mt-6'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default CompanyUserListForm;
